import { getStripe } from '@/lib'
import { Button, FormDialog, logger, SelectField, SubtleLoader } from '@/ui'
import {
	type BillingProfileData,
	paymentMethodIndexQueryKey,
	usePaymentMethodSetupIntent,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NicknameField } from './NicknameField'

export interface AddedPaymentMethod {
	stripePaymentMethodId: string
	nickname: string | null
}

export interface AddCardDialogProps {
	organisationId: string
	open: boolean
	onClose: () => void
	onCardAdded: (added: AddedPaymentMethod) => void
	/** Lock the card to a specific billing profile and hide the profile selector. */
	profileId?: string
}

function profileLabel(profile: BillingProfileData): string {
	return profile.name ?? profile.stripeCustomerId
}

export function AddCardDialog({ organisationId, open, onClose, onCardAdded, profileId }: AddCardDialogProps) {
	const { t } = useTranslation('common')
	const [clientSecret, setClientSecret] = useState<string | null>(null)
	const [publishableKey, setPublishableKey] = useState<string | null>(null)
	const [fetchError, setFetchError] = useState('')
	const [selectedProfileId, setSelectedProfileId] = useState<string | null>(profileId ?? null)

	const profilesQuery = useProfilesIndex(organisationId, { enabled: open })
	const profiles = useMemo(() => profilesQuery.data?.data ?? [], [profilesQuery.data])

	const setupIntent = usePaymentMethodSetupIntent({
		mutationKey: ['payment-method-setup-intent'],
		onSuccess: (data) => {
			setClientSecret(data.clientSecret)
			setPublishableKey(data.publishableKey)
		},
		onError: (err) => {
			if (err?.response?.status !== 422) {
				logger.error(err, { tags: { component: 'AddCardDialog', action: 'create-setup-intent' } })
			}
			setFetchError(err?.response?.data?.message ?? t('settings.paymentMethods.addCard.fetchError'))
		},
	})

	useEffect(() => {
		if (!open) {
			setClientSecret(null)
			setPublishableKey(null)
			setFetchError('')
			setSelectedProfileId(profileId ?? null)
		}
	}, [open, profileId])

	useEffect(() => {
		if (!open || selectedProfileId !== null) {
			return
		}

		if (profileId) {
			setSelectedProfileId(profileId)
			return
		}

		if (profiles.length === 0) {
			return
		}

		const primary = profiles.find((profile) => profile.isPrimary)
		setSelectedProfileId((primary ?? profiles[0]).id)
	}, [open, profiles, selectedProfileId, profileId])

	useEffect(() => {
		if (!open || clientSecret || setupIntent.isPending || !profilesQuery.isSuccess) {
			return
		}

		if (selectedProfileId === null) {
			return
		}

		setupIntent.mutate({
			organisation: organisationId,
			data: { profileId: selectedProfileId },
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, clientSecret, selectedProfileId, profilesQuery.isSuccess])

	const handleProfileChange = (profile: BillingProfileData) => {
		if (profile.id === selectedProfileId) {
			return
		}

		setFetchError('')
		setClientSecret(null)
		setPublishableKey(null)
		setSelectedProfileId(profile.id)
	}

	const selectedProfile = profiles.find((profile) => profile.id === selectedProfileId) ?? null

	return (
		<FormDialog open={open} onClose={onClose} title={t('settings.paymentMethods.addCard.title')} maxWidth="md">
			{fetchError
				? (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm mb-4">
						<div className="font-medium text-amber-900 mb-1">
							{t('settings.paymentMethods.addCard.errorTitle')}
						</div>
						<div className="text-amber-700">{fetchError}</div>
					</div>
				)
				: null}
			{!profileId && profiles.length > 1 && selectedProfile
				? (
					<div className="mb-4">
						<SelectField<BillingProfileData>
							label={t('settings.paymentMethods.addCard.profileLabel')}
							options={profiles}
							value={selectedProfile}
							onChange={handleProfileChange}
							disabled={setupIntent.isPending}
							renderOption={(profile) => (
								<span className="flex items-center gap-2">
									<span>{profileLabel(profile)}</span>
									{profile.isPrimary
										? <span className="text-xs text-grey-500">{t('profiles.primary')}</span>
										: null}
								</span>
							)}
						/>
					</div>
				)
				: null}
			{!clientSecret
				? <SubtleLoader delay={0} />
				: (
					<Elements key={clientSecret} stripe={getStripe(publishableKey)} options={{ clientSecret }}>
						<AddCardForm organisationId={organisationId} onSuccess={onCardAdded} onCancel={onClose} />
					</Elements>
				)}
		</FormDialog>
	)
}

interface AddCardFormProps {
	organisationId: string
	onSuccess: (added: AddedPaymentMethod) => void
	onCancel: () => void
}

function AddCardForm({ organisationId, onSuccess, onCancel }: AddCardFormProps) {
	const { t } = useTranslation('common')
	const stripe = useStripe()
	const elements = useElements()
	const queryClient = useQueryClient()
	const [submitting, setSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState('')
	const [paymentComplete, setPaymentComplete] = useState(false)
	const [nickname, setNickname] = useState('')

	const handleChange = (event: StripePaymentElementChangeEvent) => {
		setPaymentComplete(event.complete)
	}

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault()

		if (!stripe || !elements) {
			return
		}

		setSubmitting(true)
		setSubmitError('')

		const result = await stripe.confirmSetup({
			elements,
			confirmParams: {
				return_url: window.location.href,
			},
			redirect: 'if_required',
		})

		if (result.error) {
			logger.error(result.error, { tags: { component: 'AddCardForm', action: 'confirm-setup' } })
			setSubmitError(result.error.message ?? t('settings.paymentMethods.addCard.confirmError'))
			setSubmitting(false)
			return
		}

		const stripePaymentMethodId = typeof result.setupIntent.payment_method === 'string'
			? result.setupIntent.payment_method
			: result.setupIntent.payment_method?.id ?? null

		if (!stripePaymentMethodId) {
			logger.error(new Error('SetupIntent succeeded but no payment_method id was returned'), {
				tags: { component: 'AddCardForm', action: 'confirm-setup' },
			})
			setSubmitError(t('settings.paymentMethods.addCard.confirmError'))
			setSubmitting(false)
			return
		}

		logger.track('Payment method: card added')
		queryClient.invalidateQueries({ queryKey: paymentMethodIndexQueryKey(organisationId) })
		setSubmitting(false)
		onSuccess({
			stripePaymentMethodId,
			nickname: nickname.trim() === '' ? null : nickname.trim(),
		})
	}

	const disabled = !stripe || !elements || submitting || !paymentComplete

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<NicknameField value={nickname} onChange={setNickname} disabled={submitting} />
			<PaymentElement
				onChange={handleChange}
				options={{
					layout: 'tabs',
					paymentMethodOrder: ['card', 'sepa_debit'],
					wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
				}}
			/>

			{submitError
				? (
					<div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
						<div className="font-medium text-amber-900 mb-1">
							{t('settings.paymentMethods.addCard.errorTitle')}
						</div>
						<div className="text-amber-700">{submitError}</div>
					</div>
				)
				: null}

			<div className="flex justify-end gap-2 pt-2 border-t border-grey-100 -mx-6 px-6 pt-4 mt-2">
				<Button
					type="subtle"
					size="short"
					onClick={onCancel}
					disabled={submitting}
				>
					{t('settings.paymentMethods.addCard.cancel')}
				</Button>
				<Button
					type="committal"
					size="short"
					onClick={handleSubmit}
					disabled={disabled}
					isLoading={submitting}
				>
					{t('settings.paymentMethods.addCard.save')}
				</Button>
			</div>
		</form>
	)
}
